/**
 * Converts a string value to a boolean.
 * @param {string | null | undefined} value - The input value to convert.
 * @param {boolean} [defaultValue=false] - The default boolean value if the input is null or undefined.
 * @returns {boolean} - Returns `true` if the value is `'true'` (case-insensitive), otherwise `false`.
 */

import { TFunction } from 'i18next'
import { ExternalLinks } from 'constants/links'
import { AgreementLink, EmbeddedCustomerAgreement } from './types'

export const toBoolean = (value: string | null | undefined, defaultValue = false): boolean => {
  if (!value) return defaultValue
  return String(value)?.toLowerCase() === 'true'
}

/**
 * Validates if the given string is a valid hex color code (3 or 6 characters long).
 * @param {string} hex - The hex color code to validate (e.g., 'a3c', 'A3C8F9').
 * @returns {string} - Returns the hex code if valid, otherwise an empty string.
 */
export const isValidHexCode = (hex: string) => {
  if (!hex) return ''
  const regex = /^[A-Fa-f0-9]{3}$|^[A-Fa-f0-9]{6}$/
  return regex.test(hex) ? hex : ''
}

/**
 * Get the separator prefix when converting an array of items to a sentence.
 * @returns string
 */
export const getListToSentencePrefix = (listLength: number, itemIndex: number, t: TFunction) => {
  const andPrefix = ` ${t('and')} `
  if (listLength === 2 && itemIndex === 1) return andPrefix
  if (listLength > 2) {
    if (itemIndex === listLength - 1) return andPrefix
    if (itemIndex > 0) return ', '
  }
}

export const getAgreementLinks = (
  t: TFunction,
  agreements: EmbeddedCustomerAgreement[] = []
): AgreementLink[] => {
  const privacyPolicyUrl = ExternalLinks.PRIVACY_POLICY_URL ?? ''

  const mappedAgreements = agreements.map<AgreementLink>(({ name, displayName, url }) => ({
    id: name,
    label: displayName ?? name,
    url: url,
  }))

  if (privacyPolicyUrl) {
    mappedAgreements.push({
      id: 'privacyPolicy',
      label: t('privacyPolicy'),
      url: privacyPolicyUrl,
    })
  }
  return mappedAgreements
}
